import React from "react";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSChip from "views/system-design/DSChip";
import DSAccordion from "views/system-design/DSAccordion";
import DSAccordionSummary from "views/system-design/DSAccordionSummary";
import DSAccordionDetails from "views/system-design/DSAccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { StoryResult, StoryStepAction } from "../types";

interface StoryResultsPanelProps {
  storyResults: StoryResult[];
}

/**
 * StoryResultsPanel component - displays story execution results
 *
 * Shows:
 * - Per-story verdict with color-coded chip
 * - Expandable action history with step-by-step reasoning
 * - Final observations and error messages
 * - Step details (key events, waits, done status)
 *
 * Rendered in test results and analysis history to show completed story execution details.
 */
const StoryResultsPanel: React.FC<StoryResultsPanelProps> = ({
  storyResults,
}) => {
  // Don't render if no story results
  if (!storyResults || storyResults.length === 0) {
    return null;
  }

  // Map story verdict to chip color
  const verdictColor = (
    verdict: StoryResult["verdict"],
  ): "success" | "error" | "warning" | "default" => {
    switch (verdict) {
      case "pass":
        return "success";
      case "fail":
        return "error";
      case "error":
        return "error";
      case "timeout":
        return "warning";
      default:
        return "default";
    }
  };

  return (
    <DSBox sx={{ mt: 2, mb: 1 }}>
      <DSTypography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
        Story Results
      </DSTypography>

      <DSBox sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {storyResults.map((story, index) => (
          <DSAccordion key={`${story.storyId}-${index}`}>
            <DSAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <DSBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  width: "100%",
                }}
              >
                {/* Verdict chip */}
                <DSChip
                  label={story.verdict.toUpperCase()}
                  color={verdictColor(story.verdict)}
                  size="small"
                />

                {/* Story ID (human-readable like "clock-face-visible") */}
                <DSTypography sx={{ flex: 1 }}>{story.storyId}</DSTypography>

                {/* Duration and step count */}
                <DSTypography variant="body2" color="text.secondary">
                  {story.totalSteps} steps, {story.durationMs}ms
                </DSTypography>
              </DSBox>
            </DSAccordionSummary>

            <DSAccordionDetails>
              <DSBox
                sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
              >
                {/* Final observation */}
                {story.finalObservation && (
                  <DSTypography
                    variant="body2"
                    sx={{ mb: 1, fontStyle: "italic" }}
                  >
                    {story.finalObservation}
                  </DSTypography>
                )}

                {/* Error message (if verdict is error) */}
                {story.verdict === "error" && story.error && (
                  <DSTypography variant="body2" sx={{ color: "error.main" }}>
                    Error: {story.error}
                  </DSTypography>
                )}

                {/* Action history section */}
                <DSTypography variant="subtitle2">Action History</DSTypography>

                {/* Steps list */}
                <DSBox
                  sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                >
                  {story.steps.map((step, stepIndex) => (
                    <DSBox
                      key={stepIndex}
                      sx={{
                        borderLeft: "2px solid",
                        borderColor: "grey.300",
                        pl: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      {/* Step header with number and action type */}
                      <DSBox
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <DSTypography
                          variant="body2"
                          sx={{ fontWeight: "bold", minWidth: "60px" }}
                        >
                          Step {stepIndex + 1}
                        </DSTypography>
                        <DSChip label={step.action} size="small" />

                        {/* Action-specific details */}
                        {step.action === "key_event" && step.keyCode && (
                          <DSTypography variant="body2" color="text.secondary">
                            Key: {step.keyCode}
                          </DSTypography>
                        )}
                        {step.action === "wait" && step.waitMs && (
                          <DSTypography variant="body2" color="text.secondary">
                            Wait: {step.waitMs}ms
                          </DSTypography>
                        )}
                        {step.action === "done" && step.status && (
                          <DSChip
                            label={step.status.toUpperCase()}
                            color={step.status === "pass" ? "success" : "error"}
                            size="small"
                          />
                        )}
                      </DSBox>

                      {/* Reasoning */}
                      <DSTypography variant="body2" color="text.secondary">
                        {step.reasoning.length > 200
                          ? `${step.reasoning.substring(0, 200)}...`
                          : step.reasoning}
                      </DSTypography>
                    </DSBox>
                  ))}
                </DSBox>
              </DSBox>
            </DSAccordionDetails>
          </DSAccordion>
        ))}
      </DSBox>
    </DSBox>
  );
};

export default StoryResultsPanel;
